"use client";

import { useEffect, useState, useMemo, memo } from "react";
import { useParams } from "react-router-dom";
import { CitizenForm } from "@/components/citizens/CitizenForm";
import { CitizensAPI } from "@/lib/api";
import { Citizen } from "@/types";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ArrowLeft } from "lucide-react";

const EditCitizenPage = memo(function EditCitizenPage() {
  const params = useParams();
  const navigate = useNavigate();
  const citizenId = useMemo(() => Number(params.id), [params.id]);

  const [citizen, setCitizen] = useState<Citizen | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    // Move these inside the effect to prevent stale closures
    const abortController = new AbortController();
    let isMounted = true;

    const fetchCitizen = async () => {
      if (!citizenId || isNaN(citizenId)) {
        toast.error("Invalid citizen ID");
        navigate("/citizens");
        return;
      }

      try {
        const data = await CitizensAPI.getById(citizenId);
        if (isMounted) {
          setCitizen(data);
          setIsLoading(false);
        }
      } catch (error: any) {
        if (error.name === "AbortError") {
          return;
        }
        console.error("Error fetching citizen:", error);
        if (isMounted) {
          toast.error("Failed to load citizen");
          setIsLoading(false);
        }
      }
    };

    if (citizenId) {
      setIsLoading(true);
      fetchCitizen();
    }

    return () => {
      isMounted = false;
      abortController.abort();
    };
  }, [citizenId, navigate]); // Add navigate to dependencies

  const handleSubmit = async (data: any) => {
    // Ensure image fields are null when empty
    const processedData = {
      ...data,
      main_pic: data.main_pic === "" ? null : data.main_pic,
      profile_pic: data.profile_pic === "" ? null : data.profile_pic,
      banner_pic: data.banner_pic === "" ? null : data.banner_pic,
    };

    setIsSaving(true);
    try {
      await CitizensAPI.update(citizenId, processedData);
      toast.success("Citizen updated successfully");
    } catch (error) {
      console.error("Update error:", error);
      toast.error("Failed to update citizen");
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div>
          <h1 className="text-3xl font-bold">Edit Citizen</h1>
          <p className="text-muted-foreground">Loading citizen details...</p>
        </div>
        <div className="flex h-64 items-center justify-center">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent"></div>
        </div>
      </div>
    );
  }

  if (!citizen) {
    return (
      <div className="space-y-6">
        <div>
          <h1 className="text-3xl font-bold">Citizen Not Found</h1>
          <p className="text-muted-foreground">
            The requested citizen could not be found.
          </p>
        </div>
        <Button onClick={() => navigate("/citizens")}>Back to Citizens</Button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => navigate("/citizens")}
        >
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <div className="flex items-center gap-4">
          {citizen.profile_pic && (
            <img
              src={citizen.profile_pic}
              alt={citizen.citizen_name}
              className="h-16 w-16 rounded-full object-cover"
            />
          )}
          <div>
            <h1 className="text-3xl font-bold">
              Edit Citizen: {citizen.citizen_name}
            </h1>
            <p className="text-muted-foreground">
              Update the details for this citizen
            </p>
            <p>
              <a
                className="text-blue-500 hover:text-blue-600"
                href={`${import.meta.env.VITE_FRONTEND_URL}/profile/${
                  citizen.slug
                }`}
                target="_blank"
                rel="noopener noreferrer"
              >
                View profile
              </a>
            </p>
          </div>
        </div>
      </div>

      {citizen.banner_pic && (
        <div className="relative h-48 w-full overflow-hidden rounded-lg">
          <img
            src={citizen.banner_pic}
            alt="Citizen Banner"
            className="h-full w-full object-cover"
          />
        </div>
      )}

      <Tabs defaultValue="details" className="w-full">
        <TabsList>
          <TabsTrigger value="details">Details</TabsTrigger>
          <TabsTrigger value="images">Images</TabsTrigger>
          <TabsTrigger value="ratings">Ratings</TabsTrigger>
          <TabsTrigger value="analytics">Analytics</TabsTrigger>
          <TabsTrigger value="history">History</TabsTrigger>
        </TabsList>

        <TabsContent value="details" className="py-4">
          <div className="rounded-lg border p-6">
            <CitizenForm
              initialData={citizen}
              onSubmit={handleSubmit}
              isLoading={isSaving}
            />
          </div>
        </TabsContent>

        <TabsContent value="images" className="py-4">
          <div className="rounded-lg border p-6">
            <h2 className="text-xl font-semibold mb-4">Citizen Images</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <h3 className="font-medium">Main Picture</h3>
                {citizen.main_pic ? (
                  <div className="relative aspect-video overflow-hidden rounded-lg">
                    <img
                      src={citizen.main_pic}
                      alt="Main Picture"
                      className="h-full w-full object-cover"
                    />
                  </div>
                ) : (
                  <div className="flex aspect-video items-center justify-center rounded-lg border border-dashed">
                    <p className="text-muted-foreground">No main picture</p>
                  </div>
                )}
              </div>

              <div className="space-y-2">
                <h3 className="font-medium">Profile Picture</h3>
                {citizen.profile_pic ? (
                  <div className="relative aspect-square w-48 overflow-hidden rounded-full">
                    <img
                      src={citizen.profile_pic}
                      alt="Profile Picture"
                      className="h-full w-full object-cover"
                    />
                  </div>
                ) : (
                  <div className="flex aspect-square w-48 items-center justify-center rounded-full border border-dashed">
                    <p className="text-muted-foreground">No profile picture</p>
                  </div>
                )}
              </div>

              <div className="space-y-2 md:col-span-2">
                <h3 className="font-medium">Banner Picture</h3>
                {citizen.banner_pic ? (
                  <div className="relative aspect-[21/9] overflow-hidden rounded-lg">
                    <img
                      src={citizen.banner_pic}
                      alt="Banner Picture"
                      className="h-full w-full object-cover"
                    />
                  </div>
                ) : (
                  <div className="flex aspect-[21/9] items-center justify-center rounded-lg border border-dashed">
                    <p className="text-muted-foreground">No banner picture</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </TabsContent>

        <TabsContent value="ratings">
          <div className="rounded-lg border p-6">
            <h2 className="text-xl font-semibold mb-4">Citizen Ratings</h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {citizen.citizen_ratings &&
                Object.entries(citizen.citizen_ratings)
                  .filter(([key]) => key.endsWith("_score"))
                  .map(([key, value]) => (
                    <div key={key} className="rounded-md border p-4">
                      <p className="text-sm text-muted-foreground">
                        {key
                          .replace("_score", "")
                          .split("_")
                          .map(
                            (word) =>
                              word.charAt(0).toUpperCase() + word.slice(1)
                          )
                          .join(" ")}
                      </p>
                      <p className="text-2xl font-bold">
                        {Number(value).toFixed(1)}
                      </p>
                    </div>
                  ))}
            </div>
            <div className="mt-4">
              <p className="text-sm text-muted-foreground">
                Total Ratings: {citizen.citizen_ratings?.total_ratings || 0}
              </p>
            </div>
          </div>
        </TabsContent>

        <TabsContent value="analytics">
          <div className="rounded-lg border p-6">
            <h2 className="text-xl font-semibold mb-4">Analytics</h2>
            <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
              <div className="rounded-md border p-4">
                <p className="text-sm text-muted-foreground">Total Likes</p>
                <p className="text-2xl font-bold">
                  {citizen.total_likes.toLocaleString()}
                </p>
              </div>
              <div className="rounded-md border p-4">
                <p className="text-sm text-muted-foreground">Total Dislikes</p>
                <p className="text-2xl font-bold">
                  {citizen.total_dislikes.toLocaleString()}
                </p>
              </div>
              <div className="rounded-md border p-4">
                <p className="text-sm text-muted-foreground">Score</p>
                <p className="text-2xl font-bold">
                  {citizen.score !== null ? citizen.score.toFixed(1) : "N/A"}
                </p>
              </div>
            </div>
          </div>
        </TabsContent>

        <TabsContent value="history">
          <div className="rounded-lg border p-6">
            <h2 className="text-xl font-semibold mb-4">History</h2>
            <div className="space-y-4">
              <div className="flex items-start gap-2 rounded-md border p-4">
                <div className="h-2 w-2 mt-2 rounded-full bg-blue-500" />
                <div>
                  <p className="font-medium">Profile Created</p>
                  <p className="text-sm text-muted-foreground">
                    {new Date(citizen.created_date).toLocaleDateString()} at{" "}
                    {new Date(citizen.created_date).toLocaleTimeString()}
                  </p>
                  <p className="text-sm text-muted-foreground">
                    Added by ID: {citizen.added_by}
                  </p>
                </div>
              </div>
              {citizen.modified_date && (
                <div className="flex items-start gap-2 rounded-md border p-4">
                  <div className="h-2 w-2 mt-2 rounded-full bg-green-500" />
                  <div>
                    <p className="font-medium">Profile Updated</p>
                    <p className="text-sm text-muted-foreground">
                      {new Date(citizen.modified_date).toLocaleDateString()} at{" "}
                      {new Date(citizen.modified_date).toLocaleTimeString()}
                    </p>
                    {citizen.modified_by && (
                      <p className="text-sm text-muted-foreground">
                        Modified by ID: {citizen.modified_by}
                      </p>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
});

export default EditCitizenPage;
